// src/components/ui/QuotasCreator.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { LoaderCircle, Target, Plus, Save, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import API_CONFIG from '../../config/api';
import { parsePydanticError } from '../../lib/utils'

const BASE_URL = API_CONFIG.BASE_URL;

// Enum definitions
const DATA_TYPES = ['STRING', 'INTEGER', 'FLOAT', 'BOOLEAN', 'LIST_STRING', 'LIST_INTEGER', 'LIST_FLOAT'];
const ENFORCEMENT_TYPES = ['HARD', 'SOFT'];
const LOGICAL_GROUPS = ['AND', 'OR'];
const OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'IN', 'NOT IN'];

function QuotasCreator({ surveyId }) {
	const navigate = useNavigate();
	const [quotasObject, setQuotasObject] = useState([]);
	const [originalQuotas, setOriginalQuotas] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState(null);
	const [saveResults, setSaveResults] = useState(null);
	const [hasChanges, setHasChanges] = useState(false);
	const { authenticatedFetch } = useAuthenticatedApi();

	const fetchQuotas = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const url = BASE_URL + `/active_surveys/quotas/list-quotas/${surveyId}`;
			const result = await authenticatedFetch(url, {}, 'GET');
			
			if (result.status === 404) {
				navigate('/404', { state: { message: `survey with survey_id: ${surveyId} is not found`} });
				return;
			}

			if (result.status === 401) {
				setError("You are not authorized to access this survey");
				setQuotasObject([]);
				return;
			}

			if (!result.ok) {
				let errorMessage = `Failed to fetch quotas: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {}
				setError(errorMessage);
				return;
			}

			const datas = await result.json();
			if (datas) {
				const quotas = datas.quotas || [];
				setQuotasObject(quotas);
				setOriginalQuotas(JSON.parse(JSON.stringify(quotas))); // Deep copy
			} else {
				setError("Error API returned empty or invalid data structure");
			}

		} catch (err) {
			console.error("Network error:", err);
			setError(`Failed to load quotas. Please check your connection`);
			setQuotasObject([]);
		} finally {
			setIsLoading(false);
		}
	}, [surveyId]);

	useEffect(() => {
		fetchQuotas();
	}, [fetchQuotas]);

	useEffect(() => {
		// Check if there are changes
		const changed = JSON.stringify(quotasObject) !== JSON.stringify(originalQuotas);
		setHasChanges(changed);
	}, [quotasObject, originalQuotas]);

	const addNewQuota = () => {
		const newQuota = {
			quota_id: null, // null means insert
			parent_quota_id: null,
			label: '',
			conditions: [],
			target_count: 0,
			current_count: 0,
			enforcement_type: 'HARD',
			priority: 0
		};
		setQuotasObject([...quotasObject, newQuota]);
	};

	const addCondition = (quotaIndex) => {
		const newCondition = {
			condition_id: null,
			field_name: '',
			operator: '=',
			value: '',
			data_type: 'STRING',
			logical_group: 'AND'
		};
		const updated = [...quotasObject];
		updated[quotaIndex].conditions.push(newCondition);
		setQuotasObject(updated);
	};

	const updateCondition = (quotaIndex, conditionIndex, field, value) => {
		const updated = [...quotasObject];
		updated[quotaIndex].conditions[conditionIndex][field] = value;
		setQuotasObject(updated);
	};

	const updateQuotaField = (quotaIndex, field, value) => {
		const updated = [...quotasObject];
		if (field === "parent_quota_id" && value === "")
			updated[quotaIndex][field] = null;
		else
			updated[quotaIndex][field] = value;


		setQuotasObject(updated);
	};

	const deleteQuota = async (quotaIndex) => {
		const quota = quotasObject[quotaIndex];
		
		// If it's a new quota (not saved yet), just remove from state
		if (!quota.quota_id) {
			const updated = quotasObject.filter((_, idx) => idx !== quotaIndex);
			setQuotasObject(updated);
			return;
		}

		// Confirm deletion
		if (!window.confirm(`Are you sure you want to delete quota "${quota.label || quota.quota_id}"? This action cannot be undone.`)) {
			return;
		}

		try {
			const url = BASE_URL + `/active_surveys/quotas/delete-quota/${surveyId}/${quota.quota_id}`;
			const result = await authenticatedFetch(url, {}, 'DELETE');

			if (!result.ok) {
				let errorMessage = `Failed to delete quota: ${result.status}`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {}
				setError(errorMessage);
				return;
			}

			// Success - refresh the list
			await fetchQuotas();
			
		} catch (err) {
			console.error("Error deleting quota:", err);
			setError("Failed to delete quota. Please try again.");
		}
	};

	const deleteCondition = async (quotaIndex, conditionIndex) => {
		const quota = quotasObject[quotaIndex];
		const condition = quota.conditions[conditionIndex];

		// If it's a new condition (not saved yet), just remove from state
		if (!condition.condition_id) {
			const updated = [...quotasObject];
			updated[quotaIndex].conditions = updated[quotaIndex].conditions.filter((_, idx) => idx !== conditionIndex);
			setQuotasObject(updated);
			return;
		}

		// Check if this is the last condition
		if (quota.conditions.length <= 1) {
			setError("Cannot delete the last condition. Quotas must have at least one condition.");
			return;
		}

		// Confirm deletion
		if (!window.confirm(`Are you sure you want to delete this condition? This action cannot be undone.`)) {
			return;
		}

		try {
			const url = BASE_URL + `/active_surveys/quotas/delete-quota-condition/${surveyId}/${quota.quota_id}/${condition.condition_id}`;
			const result = await authenticatedFetch(url, {}, 'DELETE');

			if (!result.ok) {
				let errorMessage = `Failed to delete condition: ${result.status}`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {}
				setError(errorMessage);
				return;
			}

			// Success - refresh the list
			await fetchQuotas();
			
		} catch (err) {
			console.error("Error deleting condition:", err);
			setError("Failed to delete condition. Please try again.");
		}
	};

	const saveQuotas = async () => {
		setIsSaving(true);
		setError(null);
		setSaveResults(null);

		try {
			const url = BASE_URL + `/active_surveys/quotas/upsert-quotas/${surveyId}`;
			const payload = {
				survey_id: surveyId,
				quotas: quotasObject
			};

			const result = await authenticatedFetch(url, {
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			}, 'POST');
			
			if (result.status === 422) {
				let errorMessage = `Failed to save quotas: ${result.status}`;
				try {

					const errorBody = await result.json();
					if (errorBody) {
						errorMessage = parsePydanticError(errorBody)
					}
				} catch (jsonError) {}
				setError(errorMessage);
				return
			
			}

			if (!result.ok) {
				let errorMessage = `Failed to save quotas: ${result.status}`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {}
				setError(errorMessage);
				return;
			}

			const response = await result.json();
			
			// Store the results to display
			setSaveResults(response.statuses);
			
			// Check if all were accepted
			const allAccepted = response.statuses.every(s => s.accepted);
			if (allAccepted) {
				// Refresh the data only if all succeeded
				await fetchQuotas();
				setHasChanges(false);
			}
			
		} catch (err) {
			console.error("Error saving quotas:", err);
			setError("Failed to save quotas. Please try again.");
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-12">
				<LoaderCircle className="h-8 w-8 animate-spin text-blue-500" />
				<span className="ml-2 text-gray-600">Loading quotas...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="text-center py-12">
				<p className="text-red-500 mb-4">{error}</p>
				<Button onClick={fetchQuotas} variant="outline">Retry</Button>
			</div>
		);
	}

	return (
		<div className="relative">
			{/* Save Button - Fixed at top */}
			<div className="sticky top-0 z-10 bg-white border-b pb-4 mb-4 flex justify-between items-center">
				<h3 className="text-lg font-semibold text-gray-800">
					Edit Quotas ({quotasObject.length})
				</h3>
				<Button
					variant={"none"}
					onClick={saveQuotas}
					disabled={!hasChanges || isSaving}
					className={`flex items-center gap-2 ${
						hasChanges ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-gray-200 text-gray-500'
					}`}
				>
					<Save className="h-4 w-4" />
					{isSaving ? 'Saving...' : 'Save Changes'}
				</Button>
			</div>

			{/* Save Results */}
			{saveResults && (
				<div className="mb-4 space-y-2">
					{saveResults.map((result, idx) => (
						<div
							key={idx}
							className={`p-3 rounded-lg border ${
								result.accepted
									? 'bg-green-50 border-green-200'
									: 'bg-red-50 border-red-200'
							}`}
						>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<span className="font-mono text-xs">
										{result.quota_id || 'New Quota'}
									</span>
									<span className={`ml-2 text-xs font-semibold ${
										result.accepted ? 'text-green-700' : 'text-red-700'
									}`}>
										{result.accepted ? '✓ Success' : '✗ Failed'}
									</span>
									<span className="ml-2 text-xs text-gray-600">
										({result.update ? 'Updated' : 'Inserted'})
									</span>
								</div>
								{result.detail && (
									<p className="text-xs text-red-600 ml-2">{result.detail}</p>
								)}
							</div>
						</div>
					))}
					<Button
						onClick={() => setSaveResults(null)}
						variant="outline"
						className="text-xs"
					>
						Dismiss Results
					</Button>
				</div>
			)}

			{/* Add Quota Button - Top */}
			<div className="mb-4"> 
				<Button onClick={addNewQuota} variant="none" className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-gray-50 border-dashed border-2 border-gray-400"> 
					<Plus className="h-4 w-4" /> Add New Quota 
				</Button> 
			</div>

			{/* Scrollable Content */}
			<div className="h-[600px] overflow-y-auto pr-2 space-y-6">
				{quotasObject.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed">
						<Target className="h-12 w-12 text-gray-400 mb-2" />
						<p className="text-gray-500">No quotas yet. Click "Add New Quota" to start.</p>
					</div>
				) : (
					quotasObject.map((quota, qIdx) => (
						<QuotaEditor
							key={qIdx}
							quota={quota}
							quotaIndex={qIdx}
							updateQuotaField={updateQuotaField}
							updateCondition={updateCondition}
							addCondition={addCondition}
							deleteQuota={deleteQuota}
							deleteCondition={deleteCondition}
							allQuotas={quotasObject}
						/>
					))
				)}
			</div>

			{/* Add Quota Button - Bottom */}
			<div className="mt-4">
				<Button onClick={addNewQuota} variant="none" className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-gray-50 border-dashed border-2 border-gray-400">
					<Plus className="h-4 w-4" /> Add New Quota
				</Button>
			</div>
		</div>
	);
}

// Individual Quota Editor Component
function QuotaEditor({ quota, quotaIndex, updateQuotaField, updateCondition, addCondition, deleteQuota, deleteCondition, allQuotas }) {

	const EditableCell = ({ field, value, type = 'text', options = null }) => {
		const [localValue, setLocalValue] = useState(value ?? '');
	  
		const commitChange = () => {
		  let val = localValue;
		  if (type === 'number') val = parseInt(localValue) || 0;
		  updateQuotaField(quotaIndex, field, val);
		};

		if (options) {
			// Dropdown select
			return (
				<select
					value={value || ''}
					onChange={(e) => updateQuotaField(quotaIndex, field, e.target.value)}
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary"
				>
					{field === 'parent_quota_id' && <option value="">None</option>}
					{options.map(opt => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>
			);
		}

		return (
			<input
				type={type}
				value={localValue}
				onChange={(e) => {
					const val = type === 'number' ? parseInt(e.target.value) || 0 : e.target.value;
					setLocalValue(val)
				}}
				onBlur={commitChange}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						commitChange(); // manually commit on Enter
						e.target.blur(); // optionally remove focus
					}
				}}
				onWheel={(e) => e.target.blur()} // Prevent scroll wheel from changing number
				className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
				placeholder={`Enter ${field}`}
			/>
		);
	};

	const EditableConditionCell = ({ conditionIndex, field, value, type = 'text', options = null }) => {
		const [localValue, setLocalValue] = useState(value ?? '');

		useEffect(() => {
		  setLocalValue(value ?? '');
		}, [value]); // update localValue if parent value changes
	  
		const commitChange = () => {
			let val = localValue;
			if (type === 'number') val = parseInt(localValue) || 0;
			updateCondition(quotaIndex, conditionIndex, field, val);
		};
		
		if (options) {
			return (
				<select
					value={value || ''}
					onChange={(e) => updateCondition(quotaIndex, conditionIndex, field, e.target.value)}
					className="w-full px-1 py-1 border-none bg-transparent text-xs focus:outline-none focus:bg-white focus:border focus:border-primary rounded font-mono"
				>
					{options.map(opt => (
						<option key={opt} value={opt}>{opt}</option>
					))}
				</select>
			);
		}

		return (
			<input
				type={type}
				value={localValue}
				onChange={(e) => setLocalValue(e.target.value)}
				onBlur={commitChange}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						commitChange(); // manually commit on Enter
						e.target.blur(); // optionally remove focus
					}
				}}
				className="w-full px-1 py-1 border-none bg-transparent text-xs focus:outline-none focus:bg-white focus:border focus:border-primary rounded font-mono"
				placeholder={field}
			/>
		);
	};

	const parentOptions = allQuotas
		.filter(q => q.quota_id && q.quota_id !== quota.quota_id)
		.map(q => ({ value: q.quota_id, label: q.label || q.quota_id }));

	return (
		<div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm">
			{/* Quota Header */}
			<div className="flex justify-between items-center mb-3">
				<div className="flex items-center gap-2">
					<span className="font-mono text-xs text-gray-500">
						{quota.quota_id ? `ID: ${quota.quota_id}` : 'NEW QUOTA'}
					</span>
					{!quota.quota_id && (
						<span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">New</span>
					)}
				</div>
				<Button
					onClick={() => deleteQuota(quotaIndex)}
					variant="ghost"
					className="text-red-500 hover:text-red-700 p-1"
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			</div>

			{/* Quota Fields Table */}
			<table className="w-full border border-gray-300 text-sm mb-4">
				<thead className="bg-gray-100">
					<tr>
						<th className="border border-gray-300 px-2 py-1 text-left font-mono text-xs">Field</th>
						<th className="border border-gray-300 px-2 py-1 text-left font-mono text-xs">Value</th>
					</tr>
				</thead>
				<tbody className="font-mono text-xs">
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">parent_quota_id</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell
								field="parent_quota_id"
								value={quota.parent_quota_id || ''}
								options={[...parentOptions]}
							/>
						</td>
					</tr>
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">label</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell field="label" value={quota.label} />
						</td>
					</tr>
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">target_count</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell field="target_count" value={quota.target_count} type="number" />
						</td>
					</tr>
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">current_count</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell field="current_count" value={quota.current_count} type="number" />
						</td>
					</tr>
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">enforcement_type</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell
								field="enforcement_type"
								value={quota.enforcement_type}
								options={ENFORCEMENT_TYPES.map(t => ({ value: t, label: t }))}
							/>
						</td>
					</tr>
					<tr>
						<td className="border border-gray-300 px-2 py-1 bg-gray-50">priority</td>
						<td className="border border-gray-300 px-2 py-1">
							<EditableCell field="priority" value={quota.priority} type="number" />
						</td>
					</tr>
				</tbody>
			</table>

			{/* Conditions Section */}
			<div className="ml-8 mt-4">
				<div className="flex justify-between items-center mb-2">
					<h4 className="font-mono text-xs font-semibold text-gray-700">
						├─ conditions ({quota.conditions.length})
					</h4>
				</div>

				{quota.conditions.length > 0 && (
					<table className="w-full border border-gray-300 text-xs mb-2">
						<thead className="bg-gray-50">
							<tr>
								<th className="border border-gray-300 px-2 py-1 text-left font-mono">field_name</th>
								<th className="border border-gray-300 px-2 py-1 text-left font-mono">operator</th>
								<th className="border border-gray-300 px-2 py-1 text-left font-mono">value</th>
								<th className="border border-gray-300 px-2 py-1 text-left font-mono">data_type</th>
								<th className="border border-gray-300 px-2 py-1 text-left font-mono">logical_group</th>
							</tr>
						</thead>
						<tbody>
							{quota.conditions.map((cond, cIdx) => {
								const isNewCondition = !cond.condition_id;
								return (
									<tr key={cIdx} className={`font-mono text-xs ${isNewCondition ? 'bg-green-50' : ''}`}>
										<td className="border border-gray-300 px-2 py-1">
											{isNewCondition ? (
												<EditableConditionCell
													conditionIndex={cIdx}
													field="field_name"
													value={cond.field_name}
												/>
											) : (
												<span className="text-gray-600">{cond.field_name}</span>
											)}
										</td>
										<td className="border border-gray-300 px-2 py-1">
											{isNewCondition ? (
												<EditableConditionCell
													conditionIndex={cIdx}
													field="operator"
													value={cond.operator}
													options={OPERATORS}
												/>
											) : (
												<span className="text-gray-600">{cond.operator}</span>
											)}
										</td>
										<td className="border border-gray-300 px-2 py-1">
											{isNewCondition ? (
												<EditableConditionCell
													conditionIndex={cIdx}
													field="value"
													value={cond.value}
												/>
											) : (
												<span className="text-gray-900">{cond.value}</span>
											)}
										</td>
										<td className="border border-gray-300 px-2 py-1">
											{isNewCondition ? (
												<EditableConditionCell
													conditionIndex={cIdx}
													field="data_type"
													value={cond.data_type}
													options={DATA_TYPES}
												/>
											) : (
												<span className="text-blue-600">{cond.data_type}</span>
											)}
										</td>
										<td className="border border-gray-300 px-2 py-1">
											{isNewCondition ? (
												<EditableConditionCell
													conditionIndex={cIdx}
													field="logical_group"
													value={cond.logical_group}
													options={LOGICAL_GROUPS}
												/>
											) : (
												<span className="text-primary">{cond.logical_group}</span>
											)}
										</td>
										<td className="border border-gray-300 px-2 py-1 text-center">
											<button
												onClick={() => deleteCondition(quotaIndex, cIdx)}
												className="text-red-500 hover:text-red-700 p-1"
												title="Delete condition"
											>
												<Trash2 className="h-3 w-3 inline" />
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}

				<Button
					onClick={() => addCondition(quotaIndex)}
					variant="none"
					className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-gray-50 border border-dashed border-gray-400 text-xs"
				>
					<Plus className="h-3 w-3" /> Add Condition
				</Button>
			</div>
		</div>
	);
}

export default QuotasCreator;